import { z } from 'zod';

import { MEMBER_STATUSES } from '@/features/members';
import { dateOnlySchema, timestampSchema } from '@/types/schemas';
import { fullName } from '@/utils/text';

/** The member a machine user is linked to. */
export const linkedMemberSchema = z.object({
  id: z.number(),
  memberCode: z.string(),
  firstName: z.string(),
  lastName: z.string().nullable(),
  status: z.enum(MEMBER_STATUSES),
  photoPath: z.string().nullable(),
});
export type LinkedMember = z.infer<typeof linkedMemberSchema>;

/** One entry through the door: a face or finger match on the machine. */
export const punchSchema = z.object({
  id: z.number(),
  punchedAt: timestampSchema,
  deviceUserId: z.string(),
  name: z.string().nullable(),
  member: linkedMemberSchema.nullable(),
  verifyMode: z.string().nullable(),
  source: z.enum(['realtime', 'pull']),
});
export type Punch = z.infer<typeof punchSchema>;

/** One person's entries on one day. */
export const checkInSchema = z.object({
  deviceUserId: z.string(),
  name: z.string().nullable(),
  member: linkedMemberSchema.nullable(),
  firstPunchAt: timestampSchema,
  lastPunchAt: timestampSchema,
  punches: z.number(),
});
export type CheckIn = z.infer<typeof checkInSchema>;

export const checkInsSchema = z.object({
  date: dateOnlySchema,
  items: z.array(checkInSchema),
});
export type CheckIns = z.infer<typeof checkInsSchema>;

/** What the server may change on the machine; the UI offers only these actions. */
export const DEVICE_CONTROLS = ['access', 'clock', 'enrol', 'restart'] as const;
export type DeviceControl = (typeof DEVICE_CONTROLS)[number];

export const deviceStatusSchema = z.object({
  id: z.number(),
  serialNo: z.string(),
  model: z.string().nullable(),
  firmware: z.string().nullable(),
  userCount: z.number().nullable(),
  logCount: z.number().nullable(),
  lastSeenAt: timestampSchema.nullable(),
  lastSyncAt: timestampSchema.nullable(),
  clockOffsetSeconds: z.number().nullable(),
  isOnline: z.boolean(),
  // Unknown switches from a newer server are dropped rather than failing the screen.
  controls: z
    .array(z.string())
    .transform((items) =>
      items.filter((item): item is DeviceControl =>
        (DEVICE_CONTROLS as readonly string[]).includes(item),
      ),
    ),
});
export type DeviceStatus = z.infer<typeof deviceStatusSchema>;

/** Someone enrolled on the machine, and the member they are linked to. */
export const machineUserSchema = z.object({
  id: z.number(),
  deviceUserId: z.string(),
  name: z.string().nullable(),
  member: linkedMemberSchema.nullable(),
  accessEnabled: z.boolean().nullable(),
  accessSyncedAt: timestampSchema.nullable(),
  lastPunchAt: timestampSchema.nullable(),
});
export type MachineUser = z.infer<typeof machineUserSchema>;

export const memberAttendanceSchema = z.object({
  machineUser: machineUserSchema.nullable(),
  lastVisitAt: timestampSchema.nullable(),
  visitsThisMonth: z.number(),
  recentVisits: z.array(timestampSchema),
});
export type MemberAttendance = z.infer<typeof memberAttendanceSchema>;

export const attendanceSummarySchema = z.object({
  date: dateOnlySchema,
  inToday: z.number(),
  activeMembers: z.number(),
  activeLinked: z.number(),
  notSeen7: z.number(),
  notSeen14: z.number(),
});
export type AttendanceSummary = z.infer<typeof attendanceSummarySchema>;

export const inactiveMemberSchema = z.object({
  member: linkedMemberSchema.extend({ mobile: z.string() }),
  lastVisitAt: timestampSchema.nullable(),
});
export type InactiveMember = z.infer<typeof inactiveMemberSchema>;

export const hourCountSchema = z.object({ hour: z.number(), arrivals: z.number() });
export type HourCount = z.infer<typeof hourCountSchema>;

export const commandProgressSchema = z.object({
  id: z.number(),
  code: z.string(),
  status: z.enum(['pending', 'sent', 'done', 'failed']),
  resultNote: z.string().nullable(),
});
export type CommandProgress = z.infer<typeof commandProgressSchema>;

/** How far back the "not seen" list looks. */
export const INACTIVE_DAYS = [7, 14, 30] as const;
export type InactiveDays = (typeof INACTIVE_DAYS)[number];

/**
 * A person as the screen names them: the linked member's name, else the name on the machine,
 * else the machine's own id.
 */
export function personLabel(person: {
  member: LinkedMember | null;
  name: string | null;
  deviceUserId: string;
}): string {
  if (person.member) return fullName(person.member.firstName, person.member.lastName);
  return person.name ?? `User ${person.deviceUserId}`;
}
